import { Select, Typography } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { useGetServiceRequestTypes } from 'src/hooks/serviceRequest';
import { ServiceRequestType } from 'src/models';
import { api, getApiErrorMessage } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { SuperAdminContext } from 'src/components';

type FaxServiceRequestMappingResponse = {
  service_request_type?: ServiceRequestType;
};

const FaxServiceRequestMappingPage = () => {
  const queryClient = useQueryClient();
  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const headers = useMemo(
    () => ({
      ...(superAdminHeaders || {}),
    }),
    [superAdminHeaders]
  );

  const { data: serviceRequestTypes = [], isLoading: isLoadingTypes } =
    useGetServiceRequestTypes();

  const { data: mappedServiceRequestType, isLoading: isLoadingMapping } =
    useQuery({
      queryKey: ['fax-service-request-mapping', orgUuid],
      enabled: Boolean(orgUuid),
      queryFn: async () => {
        try {
          const response = await api.get<FaxServiceRequestMappingResponse>(
            '/v2/fax-numbers/service-request-mapping',
            { headers }
          );
          return response.data?.service_request_type || null;
        } catch (error: any) {
          if (error?.response?.status === 404) {
            return null;
          }
          throw error;
        }
      },
    });

  const [selectedTypeName, setSelectedTypeName] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    setSelectedTypeName(undefined);
  }, [orgUuid]);

  useEffect(() => {
    if (!orgUuid) {
      return;
    }
    if (isLoadingMapping) {
      return;
    }
    setSelectedTypeName(mappedServiceRequestType?.service_request_type);
  }, [mappedServiceRequestType, isLoadingMapping, orgUuid]);

  const serviceRequestTypeOptions = useMemo(() => {
    return serviceRequestTypes
      .map((type) => ({
        value: type.service_request_type,
        label: type.service_request_type,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [serviceRequestTypes]);

  const saveMappingMutation = useMutation({
    onMutate: ({ orgUuid: orgUuidAtRequest }) => {
      const previousMapping =
        queryClient.getQueryData<ServiceRequestType | null>([
          'fax-service-request-mapping',
          orgUuidAtRequest,
        ]) || null;
      return {
        orgUuid: orgUuidAtRequest,
        previousMapping,
        previousSelectedTypeName: selectedTypeName,
      };
    },
    mutationFn: async ({
      serviceRequestTypeName,
      hasMapping,
      orgUuid: _orgUuid,
    }: {
      serviceRequestTypeName: string;
      orgUuid: string;
      hasMapping: boolean;
    }) => {
      const payload = { service_request_type_name: serviceRequestTypeName };
      if (hasMapping) {
        await api.put('/v2/fax-numbers/service-request-mapping', payload, {
          headers,
        });
        return;
      }
      await api.post('/v2/fax-numbers/service-request-mapping', payload, {
        headers,
      });
    },
    onSuccess: (_, variables, context) => {
      const orgUuidAtRequest = context?.orgUuid;
      const serviceRequestTypeName = variables.serviceRequestTypeName;
      showToastSuccess('Fax referral service request mapping updated.');
      if (!orgUuidAtRequest) {
        return;
      }
      queryClient.setQueryData<ServiceRequestType | null>(
        ['fax-service-request-mapping', orgUuidAtRequest],
        (oldData) => {
          if (!oldData) {
            const matchedType = serviceRequestTypes.find(
              (type) => type.service_request_type === serviceRequestTypeName
            );
            return matchedType || oldData;
          }
          return {
            ...oldData,
            service_request_type: serviceRequestTypeName,
          };
        }
      );
      queryClient.invalidateQueries({
        queryKey: ['fax-service-request-mapping', orgUuidAtRequest],
      });
    },
    onError: (error: any, _variables, context) => {
      showToastError(
        getApiErrorMessage(error) ||
          error?.message ||
          'Failed to update fax referral mapping.'
      );
      const fallbackTypeName =
        context?.previousMapping?.service_request_type ||
        context?.previousSelectedTypeName;
      setSelectedTypeName(fallbackTypeName);
      if (!context?.orgUuid) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['fax-service-request-mapping', context?.orgUuid],
      });
    },
  });

  const handleChange = (value: string) => {
    if (!orgUuid) {
      return;
    }
    if (saveMappingMutation.isPending || isLoadingMapping) {
      return;
    }
    setSelectedTypeName(value);
    const hasMapping = Boolean(mappedServiceRequestType?.service_request_type);
    saveMappingMutation.mutate({
      serviceRequestTypeName: value,
      orgUuid,
      hasMapping,
    });
  };

  return (
    <div className="bg-white rounded-lg p-6 w-full">
      <Typography.Title level={4} className="!mb-2">
        Service request type used for creating fax referrals
      </Typography.Title>
      <div className="flex flex-col gap-2 mt-6">
        <Select
          placeholder="Select fax referral type"
          options={serviceRequestTypeOptions}
          value={selectedTypeName}
          onChange={handleChange}
          className="w-1/2"
          loading={
            isLoadingTypes || isLoadingMapping || saveMappingMutation.isPending
          }
          disabled={
            !orgUuid || isLoadingMapping || saveMappingMutation.isPending
          }
        />
      </div>
    </div>
  );
};

export default FaxServiceRequestMappingPage;
